import type { ClientAccessRestriction, ClientAccessSettings } from "@repo/shared/types";
import { Lock, ShieldUser } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./client-access-lock.i18n";

type Props = {
  restriction: ClientAccessRestriction;
  /** The client's own Private flag — tells "private" apart from "owner-only because it has no teams". */
  isPrivate: boolean;
  /** The tenant's client access settings. */
  settings: ClientAccessSettings;
  ownerName?: string | null;
  /** Omit when the team names are not known; an empty array falls back to the generic text. */
  teamNames?: string[];
  className?: string;
};

/**
 * QL-380 — marks a restricted client. Lock: the Private flag hides the client (Private
 * clients on and ticked; wins over teams). ShieldUser: limited to the owner and its teams.
 * Nothing otherwise — owner-only for lack of teams is the normal state under team access,
 * and with team access off teams are only labels.
 */
export function ClientAccessLock({
  restriction,
  isPrivate,
  settings,
  ownerName,
  teamNames,
  className,
}: Props) {
  const t = useT(T);

  const privateHidden = settings.privateEnabled && isPrivate;
  if (!privateHidden && restriction !== "team") return null;

  let label: string;
  if (privateHidden) {
    label = ownerName ? t.private(ownerName) : t.privateNoOwner;
  } else if (teamNames && teamNames.length > 0) {
    label = t.team(teamNames.join(", "));
  } else {
    label = t.teamGeneric;
  }

  const Icon = privateHidden ? Lock : ShieldUser;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="inline-flex cursor-default rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          // The trigger sits inside clickable rows; it must not open the client.
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Icon className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground", className)} />
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
